import React, { useEffect, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { mediaUrl } from "../apiUrl";
import { AuthenticatedMedia } from "./AuthenticatedMedia";
import { createEvent, deleteEvent, EventRow, fetchEvents, updateEvent } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";

const EventsPage: React.FC = () => {
  const [rows, setRows] = useState<EventRow[]>([]);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<EventRow | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  const loadEvents = async () => {
    try {
      const data = await fetchEvents();
      setRows(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch events");
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const openAddModal = () => {
    setEditing(null);
    setTitle("");
    setDescription("");
    setImage(null);
    setShowModal(true);
  };

  const openEditModal = (row: EventRow) => {
    setEditing(row);
    setTitle(row.title || "");
    setDescription(row.description || "");
    setImage(null);
    setShowModal(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      alert("Title and description are required.");
      return;
    }

    setBusy(true);
    try {
      if (editing) {
        await updateEvent(editing.id, title.trim(), description.trim(), image, editing.imageUrl);
      } else {
        await createEvent(title.trim(), description.trim(), image);
      }
      setShowModal(false);
      setEditing(null);
      setTitle("");
      setDescription("");
      setImage(null);
      await loadEvents();
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Failed to save event");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await deleteEvent(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Failed to delete event");
    }
  };

  return (
    <DashboardLayout>
      <ActionToast action={toastAction} />
      <h1 className="list-title">Events</h1>
      <p className="list-subtitle">{rows.length} total event slides</p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="list-toolbar mb-3">
        <button type="button" className="btn btn-success" onClick={openAddModal}>
          <i className="fa-solid fa-plus me-2" />
          Add Event
        </button>
      </div>
      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>Image</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{row.title}</td>
                <td>{row.description || "-"}</td>
                <td>
                  {row.imageUrl ? (
                    <AuthenticatedMedia endpoint={`/files/admin/event-slides/${row.id}`} alt={row.title} className="event-admin-image" />
                  ) : (
                    "-"
                  )}
                </td>
                <td className="text-end">
                  <div className="action-icons justify-content-end">
                    <button type="button" className="icon-btn" title="Edit" onClick={() => openEditModal(row)}>
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4 text-muted">
                  No events found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="dash-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h5 className="dash-modal-title">{editing ? "Edit Event" : "Add Event"}</h5>
              <button type="button" className="dash-modal-close" onClick={() => setShowModal(false)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <form onSubmit={onSubmit}>
              <div className="dash-modal-body">
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    className="form-control"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter event title..."
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter event description..."
                  />
                </div>
                <div>
                  <label className="form-label">Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setImage(e.target.files?.[0] || null)}
                  />
                  {editing?.imageUrl && !image && (
                    <div className="mt-2">
                      <img
                        src={mediaUrl(editing.imageUrl)}
                        alt={editing.title}
                        style={{ width: "120px", height: "72px", objectFit: "cover", borderRadius: "8px", border: "1px solid #dfe5ef" }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="dash-modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={busy}>
                  {busy ? "Saving..." : editing ? "Save Changes" : "Add Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default EventsPage;
